import getLegislationHtmlServices from '@/services/get-legislation-html-services';
import legislationServices from '@/services/legislation-services';
import { useEffect, useState } from 'react';

interface UseHtmlDocumentFetchProps {
  /**
   * Body HTML fetched during SSR. When present the text is already in the
   * server-rendered markup and this hook must not refetch it — the whole point
   * is that crawlers see the document without executing JavaScript.
   */
  initialSecHtml?: string | undefined;
  iri?: string | undefined;
  secHtmlStoragePath?: string | undefined;
  sudHtmlStoragePath?: string | undefined;
}

interface UseHtmlDocumentFetchResult {
  secHtmlContent: string;
  sudHtmlContent: string;
  loading: boolean;
  error: string | null;
}

/**
 * useHtmlDocumentFetch - Custom hook for fetching HTML document content.
 *
 * Single responsibility: Fetch secHtml and sudHtml content from storage or IRI.
 */
export function useHtmlDocumentFetch({
  initialSecHtml,
  iri,
  secHtmlStoragePath,
  sudHtmlStoragePath,
}: UseHtmlDocumentFetchProps): UseHtmlDocumentFetchResult {
  const [secHtmlContent, setSecHtmlContent] = useState<string>(
    initialSecHtml ?? ''
  );
  const [sudHtmlContent, setSudHtmlContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Server already supplied the body — refetching would re-download a large
    // document and blank the text mid-hydration.
    if (initialSecHtml) {
      return;
    }

    // Create abort controller for cleanup
    const abortController = new AbortController();
    let isMounted = true;

    if (!iri && !secHtmlStoragePath) {
      return;
    }

    const fetchContent = async () => {
      try {
        if (isMounted) {
          setLoading(true);
          setError(null);
        }

        // Check if aborted before each async operation
        if (abortController.signal.aborted) return;

        // Fetch secHtml
        if (secHtmlStoragePath) {
          try {
            const secHtml =
              await getLegislationHtmlServices.getHtmlFromStoragePath(
                secHtmlStoragePath
              );
            if (!abortController.signal.aborted && isMounted) {
              setSecHtmlContent(secHtml);
            }
          } catch (err) {
            if (!abortController.signal.aborted && isMounted) {
              console.error(
                '[useHtmlDocumentFetch] Error fetching secHtml:',
                err
              );
              setError('Failed to load main content');
            }
          }
        } else if (iri) {
          try {
            // Check if aborted
            if (abortController.signal.aborted) return;

            // Get legislation data to validate IRI
            const legislationResponse =
              await legislationServices.getLegislationHistory(iri);

            if (abortController.signal.aborted) return;

            if (!legislationResponse.data) {
              throw new Error('No legislation found for the given IRI');
            }

            const legislationData = Array.isArray(legislationResponse.data)
              ? legislationResponse.data[0]
              : legislationResponse.data;

            if (!legislationData) {
              throw new Error('Invalid legislation data returned from API');
            }

            const htmlResponse =
              await getLegislationHtmlServices.getLegislationHTML(iri);
            if (!abortController.signal.aborted && isMounted) {
              setSecHtmlContent(htmlResponse);
            }
          } catch (err) {
            if (!abortController.signal.aborted && isMounted) {
              console.error(
                '[useHtmlDocumentFetch] Error fetching IRI content:',
                err
              );
              setError(
                err instanceof Error ? err.message : 'Failed to load content'
              );
            }
          }
        }

        // Check if aborted before sudHtml fetch
        if (abortController.signal.aborted) return;

        // Fetch sudHtml independently (optional, don't fail if secHtml succeeded)
        if (sudHtmlStoragePath) {
          try {
            const sudHtml =
              await getLegislationHtmlServices.getHtmlFromStoragePath(
                sudHtmlStoragePath
              );
            if (!abortController.signal.aborted && isMounted) {
              setSudHtmlContent(sudHtml);
            }
          } catch (err) {
            if (!abortController.signal.aborted && isMounted) {
              console.warn(
                '[useHtmlDocumentFetch] sudHtml not available (normal for legacy):',
                err
              );
            }
          }
        }
      } finally {
        if (!abortController.signal.aborted && isMounted) {
          setLoading(false);
        }
      }
    };

    fetchContent();

    // Cleanup function
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [iri, secHtmlStoragePath, sudHtmlStoragePath, initialSecHtml]);

  return {
    secHtmlContent,
    sudHtmlContent,
    loading,
    error,
  };
}
